import React, { FunctionComponent } from 'react';

import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { TRANSLATION_NAMESPACE } from 'directory/listings/notification-modals/constants';
import { NestedDataCellProps, NestedDataItem } from 'directory/listings/notification-modals/types';
import { capitalizeFirstLetter } from 'directory/listings/notification-modals/utils';
import 'directory/listings/notification-modals/components/TableCells/TableCells.scss';

const DAY_ORDER: Record<string, number> = {
    Sunday: 1,
    Monday: 2,
    Tuesday: 3,
    Wednesday: 4,
    Thursday: 5,
    Friday: 6,
    Saturday: 7,
};

const getDayOrder = (day: string): number => {
    const dayName = capitalizeFirstLetter(day);

    return DAY_ORDER[dayName] || 0;
};

const isNestedDataItem = (item: any): item is NestedDataItem => {
    return typeof item === 'object' && item !== null && 'day' in item && 'hours' in item;
};

export const NestedDataCell: FunctionComponent<NestedDataCellProps> = ({ data, icon, labelKey, type = 'Main' }) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);

    if (!Array.isArray(data) || data.length === 0) {
        return (
            <div className="nested-data-cell nested-data-cell--empty">
                {icon && <i className={`${icon} me-2`} />}
                <span>—</span>
            </div>
        );
    }

    if (isNestedDataItem(data[0])) {
        const nestedData = data as NestedDataItem[];
        const sortedData = [...nestedData].sort((a, b) => getDayOrder(a.day || '') - getDayOrder(b.day || ''));

        return (
            <div className="nested-data-cell nested-data-cell--hours">
                <Container className="business-hours-container">
                    <div className="nested-data-cell__header">
                        <Row className="business-hours-row">
                            {icon && (
                                <Col md={1} className="business-hours-icon">
                                    <i className={`${icon} nested-data-cell__icon--info`} />
                                </Col>
                            )}
                            <Col md={icon ? 4 : 5} className="business-hours-label">
                                {t('TYPE')}
                            </Col>
                            <Col md={7} className="business-hours-label">
                                {t(type.toUpperCase())}
                            </Col>
                        </Row>
                    </div>
                    {sortedData.map((item: NestedDataItem) => {
                        const hoursText = item.hours === 'Closed' ? t('CLOSED') : item.hours;

                        return (
                            <Row key={item.day} className="business-hours-row">
                                {icon && <Col md={1} className="business-hours-icon" />}
                                <Col md={icon ? 4 : 5} className="business-hours-day">
                                    {capitalizeFirstLetter(item.day || '')}
                                </Col>
                                <Col md={7} className="business-hours-time">
                                    <span>{hoursText || '—'}</span>
                                </Col>
                            </Row>
                        );
                    })}
                </Container>
            </div>
        );
    }

    const stringData = data as string[];

    return (
        <div className="nested-data-cell nested-data-cell--list">
            {icon && labelKey && (
                <div className="nested-data-cell__header mb-2">
                    <i className={`${icon} me-2 nested-data-cell__icon--google`} />
                    <span className="fw-bold">{t(labelKey)}</span>
                </div>
            )}
            <div className="nested-data-cell__list">
                {stringData.map((item: string) => (
                    <span key={item} className="badge bg-light text-dark me-1 mb-1">
                        {item}
                    </span>
                ))}
            </div>
        </div>
    );
};
